// React
import { useEffect, useState, useContext } from "react";

// hooks
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetActions, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";

// components
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";

// utils
import componentPropertyMap from "./ComponentFormProperties";
import ActionTable from "../ActionTable/ActionTable";
import { byAttributeName, bySystemAttributeDesc } from "../utils";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import { getAttributes } from "../../../API/Attribute/Attribute";
import GenericTranslationDialog from "../../Entities/GenericTranslationDialog";

function GroupHeading() {

    const formProperties = componentPropertyMap.GroupHeading;
    const { user, subscription } = useContext(GlobalContext).state;
    const [properties, setProperties] = useComponentProperties();
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const entityId = usePropsStoreValue("entityId");
    const project = usePropsStoreValue("project");
    const saveComponent = useSaveComponent();
    const resetContext = useResetComponentProperties();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id)
    const resetActions = useResetActions();

    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    const { t } = useTranslation([
        "display/components/group_heading",
        "display/component_toolbar",
        "display/common",
        "attribute/property_panel",
    ])

    const [{
        isGeneralPropertiesChanged,
        isActionTableChanged
    }, setChangeProperty] = useChangeBooleans();

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (selectedComponent) {
            getData()
        }
    }, [selectedComponent])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        setIsChanged();
    }, [properties])

    // state
    const [selectedTab, setSelectedTab] = useState(0);

    // functions
    async function getData() {
        if (selectedPage.showsRecordDetails === "true") {
            const attributes = await getAttributes(entityId);
            const sortedAttributes = attributes.sort(byAttributeName).sort(bySystemAttributeDesc);
            setPropertiesUsingAttributes(attributes);
            updateGeneralFetchedValue("attributes", sortedAttributes);
        } else {
            setPropertiesUsingAttributes([]);
        }
        setLoading(false);
    }

    async function setPropertiesUsingAttributes(attributes) {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperties(property, selectedComponent[property])
            }
        }
        if (selectedPage.showsRecordDetails === "true") {
            const attribute = attributes.find(attribute => attribute._id === selectedComponent.referenceAttributeId);
            setProperties("referenceLabelAttribute", attribute);
        }
    }

    function handleTabSelect(e) {
        setSelectedTab(e.selected);
    };

    function handleInputChange(e) {
        setProperties(e.target.name, e.target.value);
    };

    function handleCheckboxChange(e) {
        setProperties(e.target.name, e.target.value);
    }

    function setLabelTranslation(data) {
        setProperties("languages", data);
        setShowLabelTranslationDialog(false);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined &&
                selectedComponent[property] !== properties[property]
            ) {
                isChanged = true;
                break;
            }
        }
        if (selectedPage.showsRecordDetails === "true") {
            if (
                selectedComponent.referenceAttributeId &&
                properties.referenceLabelAttribute &&
                selectedComponent.referenceAttributeId !== properties.referenceLabelAttribute.id
            ) {
                isChanged = true;
            }
        }
        if (properties.languages && isLanguagesChanged(properties.languages, selectedComponent.languages)) {
            isChanged = true;
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged)
    }

    function isLanguagesChanged(languages, defaultLanguages) {
        const keys = Object.keys(languages);
        for (const key of keys) {
            if (languages[key] === undefined || defaultLanguages[key] === undefined) {
                debugger;
            }
            if (
                languages[key].label !== defaultLanguages[key].label ||
                languages[key].tooltip !== defaultLanguages[key].tooltip
            ) {
                return true;
            }
        }
        return false;
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setPropertiesUsingAttributes(fetchedValues.attributes);
        }
        if (isActionTableChanged) {
            resetActions();
        }
    }

    if (loading) {
        return <ComponentFormSkeleton />
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip
                    className="flex-grow-1 h-100 edit1"
                    selected={selectedTab}
                    onSelect={handleTabSelect}
                    keepTabsMounted={true}
                >
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                <div className="form-label">{t("form_labels.label", { ns: "display/components/group_heading" })}</div>
                                <input
                                    placeholder={t("form_placeholders.label")}
                                    className="form-control w-50"
                                    name="label"
                                    maxLength={256}
                                    onChange={handleInputChange}
                                    value={properties.label}
                                    disabled={selectedComponent.hidden || (properties.useAsReference && properties.useRelationNameInLabel)}
                                />
                                {subscription.numOfLanguages > 1 &&
                                    <div className="col-12">
                                        <div className="form-label d-inline-block cursor-pointer" onClick={() => {
                                            setShowLabelTranslationDialog(true)
                                        }}>
                                            <span className="text-primary">
                                                {t("form_labels.label_translation")}
                                            </span>
                                        </div>
                                    </div>
                                }
                                {showLabelTranslationDialog && (
                                    <GenericTranslationDialog
                                        onClose={() => { setShowLabelTranslationDialog(false) }}
                                        onSave={setLabelTranslation}
                                        languages={properties.languages}
                                        project={project}
                                        fields={["label"]}
                                        type={t("form_labels.heading")}
                                    />
                                )}
                                {selectedPage.useAsReference === "true" && (
                                    <Checkbox
                                        name="useRelationNameInLabel"
                                        disabled={selectedComponent.hidden}
                                        checked={properties.useRelationNameInLabel}
                                        onChange={handleCheckboxChange}
                                        label={t("form_labels.use_relation_label", { ns: "display/components/group_heading" })}
                                        className="my-1 d-block"
                                    />
                                )}
                                {selectedPage.showsRecordDetails && (
                                    <>
                                        <div className="form-label">
                                            {t("form_labels.reference_property_label", { ns: "display/components/group_heading" })}
                                        </div>
                                        <DropDownList
                                            name="referenceLabelAttribute"
                                            disabled={selectedComponent.hidden}
                                            className="form-control-custom w-25"
                                            data={fetchedValues.attributes}
                                            onChange={handleInputChange}
                                            value={properties.referenceLabelAttribute}
                                            textField="attributeName"
                                        />
                                    </>
                                )}
                                {
                                    accessedFromDesigner &&
                                    <>
                                        <Checkbox
                                            name="showEntityIcon"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showEntityIcon}
                                            onChange={handleCheckboxChange}
                                            label={t("form_labels.show_entity_icon", { ns: "display/components/group_heading" })}
                                            className="my-1 d-block"
                                        />
                                        {selectedPage.useAsReference === "false" && (
                                            <>
                                                <Checkbox
                                                    name="showCount"
                                                    checked={properties.showCount}
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleCheckboxChange}
                                                    label={t("form_labels.records_count", { ns: "display/components/group_heading" })}
                                                    className="my-1 d-block"
                                                />

                                                <Checkbox
                                                    name="showChangeView"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showChangeView}
                                                    onChange={handleCheckboxChange}
                                                    label={t("form_labels.change_list_view", { ns: "display/components/group_heading" })}
                                                    className="my-1 d-block"
                                                />
                                            </>
                                        )}
                                        {selectedPage.useAsReference === "true" && (
                                            <Checkbox
                                                name="referenceCount"
                                                disabled={selectedComponent.hidden}
                                                checked={properties.referenceCount}
                                                onChange={handleCheckboxChange}
                                                label={t("form_labels.reference_count", { ns: "display/components/group_heading" })}
                                                className="my-1 d-block"
                                            />
                                        )}
                                    </>
                                }
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
};

export default GroupHeading;